//#region lib/types/clipboard.js
/**
* Write text to the host clipboard, preferring the async Clipboard API and
* falling back to `execCommand('copy')` on hosts (jsdom, insecure contexts)
* that omit it.
* @param text - the exact text to place on the clipboard.
* @returns true only when the host accepted the write.
*/
async function writeClipboard(text) {
	if (navigator.clipboard?.writeText) try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
	const exec = typeof document.execCommand === "function" ? document.execCommand.bind(document) : void 0;
	if (exec === void 0) return false;
	const el = document.createElement("textarea");
	el.value = text;
	el.setAttribute("readonly", "");
	el.style.position = "fixed";
	el.style.left = "-9999px";
	document.body.appendChild(el);
	el.select();
	try {
		return exec("copy");
	} catch {
		return false;
	} finally {
		el.remove();
	}
}
//#endregion
//#region lib/types/markdown/highlight.js
/**
* Lightweight syntax-highlighting controller. Recognized code starts one
* dynamic engine-and-grammar request and renders plain text until it settles;
* unknown or absent language ids never request Shiki. Loading failures stay
* contained and leave the subscribed code/read surfaces on their plain arm.
*/
const LANG_ALIASES = new Map([
	["typescript", "typescript"],
	["ts", "typescript"],
	["tsx", "typescript"],
	["javascript", "typescript"],
	["js", "typescript"],
	["jsx", "typescript"],
	["shellscript", "shellscript"],
	["bash", "shellscript"],
	["sh", "shellscript"],
	["shell", "shellscript"],
	["zsh", "shellscript"],
	["json", "json"],
	["jsonc", "json"],
	["py", "python"],
	["python", "python"],
	["rb", "ruby"],
	["ruby", "ruby"],
	["go", "go"],
	["rs", "rust"],
	["rust", "rust"],
	["java", "java"],
	["c", "c"],
	["cpp", "cpp"],
	["cs", "csharp"],
	["csharp", "csharp"],
	["kotlin", "kotlin"],
	["swift", "swift"],
	["php", "php"],
	["yaml", "yaml"],
	["yml", "yaml"],
	["toml", "toml"],
	["ini", "ini"],
	["md", "markdown"],
	["markdown", "markdown"],
	["mdx", "mdx"],
	["html", "html"],
	["css", "css"],
	["scss", "scss"],
	["less", "less"],
	["sql", "sql"],
	["xml", "xml"],
	["lua", "lua"]
]);
let engine;
let enginePromise;
let engineRequests = 0;
const requestedLanguages = /* @__PURE__ */ new Map();
const listeners = /* @__PURE__ */ new Set();
let loadCount = 0;
/** Resolve and cache the Shiki implementation without propagating its failures. */
function requestLanguage(language) {
	if (engine?.isLanguageLoaded(language) === true) return true;
	if (!requestedLanguages.has(language)) {
		enginePromise ??= (() => {
			engineRequests += 1;
			return import("./highlight-engine-CGHi7i9y.js");
		})();
		const request = enginePromise.then(async (loaded) => {
			engine = loaded;
			await loaded.loadLanguage(language);
			loadCount += 1;
			for (const listener of listeners) listener();
		}).catch(() => {});
		requestedLanguages.set(language, request);
	}
	return false;
}
/**
* Subscribe to engine or grammar load completions.
* @param listener - Invoked after one requested language becomes usable.
* @returns A disposer that removes the listener.
*/
function subscribeGrammarLoaded(listener) {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}
/**
* Opaque successful-language counter for useSyncExternalStore.
* @returns The current completion count.
*/
function grammarLoadCount() {
	return loadCount;
}
/**
* Highlight code into Shiki's HTML after its engine and grammar are ready.
* @param code - Source text.
* @param lang - Markdown fence or fixed caller language hint.
* @returns Highlighted HTML, or undefined while loading or for an unsupported hint.
*/
function highlightToHtml(code, lang) {
	const resolved = lang === void 0 ? void 0 : LANG_ALIASES.get(lang.toLowerCase());
	if (resolved === void 0 || !requestLanguage(resolved)) return void 0;
	return engine?.codeToHtml(code, resolved);
}
/**
* Tokenize code into one highlighted run array per source line after Shiki is ready.
* @param code - Source text.
* @param lang - File-extension-derived language hint.
* @returns Per-line runs, or undefined while loading or for an unsupported hint.
*/
function highlightLines(code, lang) {
	const resolved = lang === void 0 ? void 0 : LANG_ALIASES.get(lang.toLowerCase());
	if (resolved === void 0 || !requestLanguage(resolved)) return void 0;
	return engine?.codeToLines(code, resolved);
}
/** Whether the language has a supported lazy grammar, without loading it.
* @param language - Fence language or file extension.
* @returns True when highlighting is supported.
*/
function supportsHighlighting(language) {
	return language !== void 0 && LANG_ALIASES.has(language.toLowerCase());
}
/** Lightweight owner; the tokenization engine is requested only for recognized code. */
var StreamingHighlightSession = class {
	delegate;
	resolve(lang) {
		const resolved = lang === void 0 ? void 0 : LANG_ALIASES.get(lang.toLowerCase());
		if (resolved === void 0 || !requestLanguage(resolved)) {
			this.delegate = void 0;
			return;
		}
		this.delegate ??= new engine.StreamingHighlightSession();
		return resolved;
	}
	updateFrame(code, lang) {
		const resolved = this.resolve(lang);
		return resolved === void 0 ? void 0 : this.delegate.updateFrame(code, resolved);
	}
	update(code, lang) {
		const resolved = this.resolve(lang);
		return resolved === void 0 ? void 0 : this.delegate.update(code, resolved);
	}
};
//#endregion
export { subscribeGrammarLoaded as a, highlightToHtml as i, grammarLoadCount as n, supportsHighlighting as o, highlightLines as r, writeClipboard as s, StreamingHighlightSession as t };

//# sourceMappingURL=highlight-BKpAd0_k.js.map